import React from "react";
import { motion } from "framer-motion";
import {
  Music2,
  Youtube,
  Instagram,
  Twitch,
  Tv,
  Camera,
  Mic2,
  Gamepad2,
} from "lucide-react";

const platforms = [
  { name: "TikTok", icon: Music2, note: "short-form virality", slug: "tiktok" },
  { name: "YouTube", icon: Youtube, note: "long-form + shorts", slug: "youtube" },
  { name: "Instagram", icon: Instagram, note: "reels · stories · IP", slug: "instagram" },
  { name: "Twitch", icon: Twitch, note: "live streaming", slug: "twitch" },
  { name: "X & Threads", icon: Tv, note: "conversation & culture", slug: "x-threads" },
  { name: "Snapchat", icon: Camera, note: "spotlight + discover", slug: "snapchat" },
  { name: "Podcast", icon: Mic2, note: "audio + video pods", slug: "podcast" },
  { name: "Kick / Gaming", icon: Gamepad2, note: "gaming & creator econ", slug: "kick-gaming" },
];

export default function Platforms() {
  return (
    <section
      id="platforms"
      data-testid="platforms-section"
      className="band bg-[#0F0F10] text-[#FAF5E8] py-20 lg:py-28"
    >
      <div className="max-w-[1500px] mx-auto px-5 lg:px-8 relative">
        {/* sticker row */}
        <div className="mb-6">
          <div className="wiggle inline-block" style={{ "--r": "4deg" }}>
            <div className="sticker bg-[#FFD500]" data-testid="platforms-sticker">
              ✦ chapter 02
            </div>
          </div>
        </div>

        {/* Section header */}
        <div className="flex flex-wrap items-end justify-between gap-6 border-b-2 border-[#FAF5E8]/30 pb-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00E5A0]">
            ② Platforms · Reach
          </div>
          <div className="font-hand text-2xl text-[#00E5A0]">
            scroll through every feed →
          </div>
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="platforms-title"
          className="font-display mt-8"
          style={{
            fontSize: "clamp(2.75rem, 9vw, 8rem)",
            lineHeight: 0.84,
            letterSpacing: "-0.02em",
          }}
        >
          No matter the <span className="text-[#00E5A0]">platform</span><br />
          you produce for —<br />
          <span className="text-outline-cream">we&apos;ve already</span><br />
          <span className="scribble" style={{ color: "#FAF5E8" }}>been there.</span>
        </motion.h2>

        {/* Lede + spec */}
        <div className="grid grid-cols-12 gap-8 mt-10">
          <div className="col-span-12 lg:col-span-7">
            <p className="text-[#FAF5E8]/85 leading-relaxed text-base lg:text-lg max-w-2xl">
              Vertical video. Long-form. Audio. Live. Static feed. Newsletter.
              Stream. <span className="bg-[#FFD500] text-[#0A0A0A] px-1.5">Whatever</span> your
              creators ship on — RTAMA has a dedicated strategist who knows the
              format inside-out. We adapt to the platform; we don&apos;t force
              you onto ours.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:border-l lg:border-[#FAF5E8]/20 lg:pl-8 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FAF5E8]/70 space-y-1.5">
            <div className="flex justify-between border-b border-[#FAF5E8]/15 py-1.5"><span>Format-agnostic</span><span className="text-[#00E5A0]">Yes</span></div>
            <div className="flex justify-between border-b border-[#FAF5E8]/15 py-1.5"><span>Region lock</span><span className="text-[#FF2D9C]">None</span></div>
            <div className="flex justify-between border-b border-[#FAF5E8]/15 py-1.5"><span>Coverage</span><span>24 / 7</span></div>
            <div className="flex justify-between py-1.5"><span>Languages</span><span>EN · ES · GR · FR</span></div>
          </div>
        </div>

        {/* Platform cards */}
        <div
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4"
          data-testid="platforms-grid"
        >
          {platforms.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 18, rotate: 0 }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                  rotate: i % 2 === 0 ? -2 : 2,
                }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                whileHover={{ rotate: 0, scale: 1.03 }}
                className="relative bg-[#FAF5E8] text-[#0A0A0A] rounded-2xl p-5 sm:p-6 border-2 border-[#FAF5E8] group hover:bg-[#00E5A0] hover:border-[#00E5A0] transition-colors duration-200"
                data-testid={`platform-${p.slug}`}
              >
                <div className="flex items-start justify-between">
                  <Icon className="w-6 h-6" />
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase opacity-60">
                    0{i + 1}
                  </span>
                </div>
                <div className="font-display text-3xl mt-12">{p.name}</div>
                <div className="font-hand text-lg mt-1 opacity-90">{p.note}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom sticker line */}
        <div className="mt-12 flex flex-wrap items-center gap-3">
          <div className="sticker bg-[#FF2D9C] text-[#FAF5E8] border-[#FAF5E8]" style={{ boxShadow: "3px 3px 0 #FAF5E8" }}>
            Any feed
          </div>
          <div className="sticker bg-[#FFD500]">Any format</div>
          <div className="sticker bg-[#00E5A0]">Any timezone</div>
          <div className="sticker bg-[#5B2BFF] text-[#FAF5E8] border-[#FAF5E8]" style={{ boxShadow: "3px 3px 0 #FAF5E8" }}>
            Any algorithm
          </div>
        </div>
      </div>
    </section>
  );
}
